// src/components/Dashboard/AlertsSection.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Alert,
  AlertTitle,
  Button,
  Chip,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Close,
  Error,
  Warning,
  Info,
} from '@mui/icons-material';
import {
  getUserAlerts,
  getAirportAlerts,
  getSystemAlerts,
  getSystemPastDueAlerts,
  DashboardApiError,
  Alert as AlertType,
} from '../../services/dashboardApi';
import { isIntegerUserId } from '../../services/authApi';
import { useModalOpen } from '../../contexts/ModalOpenContext';

/** Which alert API to call. Used for role-based home page. */
export type AlertsSectionSource =
  | 'system'           // System Admin: system alerts (e.g. 72h errors when supported)
  | 'systemPastDue'    // Exec Admin: system-wide past-due only
  | 'airport'          // Airport Manager: all airport alerts
  | 'airportPastDue'   // Airport Manager: past-due only for assigned airport
  | 'user'             // User: all alerts for the user
  | 'userPastDue';     // Fraport/Tenant/Client: past-due only for user

interface AlertsSectionProps {
  userId?: number | string; // May be GUID (string) or integer (number)
  airportId?: number;
  /** Legacy: when true and no alertSource, load system alerts (System Admin). */
  isAdmin?: boolean;
  /** Role-based alert source. When set, only that source is used; isAdmin/userId/airportId are used per source. */
  alertSource?: AlertsSectionSource;
}

export default function AlertsSection({
  userId,
  airportId,
  isAdmin,
  alertSource,
}: AlertsSectionProps) {
  const navigate = useNavigate();
  const { isModalOpen } = useModalOpen();
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false); // Default minimized
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<number>>(new Set());

  useEffect(() => {
    const loadAlerts = async () => {
      try {
        setLoading(true);
        setError(null);

        const allAlerts: AlertType[] = [];

        if (alertSource) {
          // Role-based single source
          switch (alertSource) {
            case 'system':
              try {
                const systemAlerts = await getSystemAlerts();
                allAlerts.push(...systemAlerts.alerts);
              } catch (err) {
                console.error('Error loading system alerts:', err);
              }
              break;
            case 'systemPastDue':
              try {
                const pastDue = await getSystemPastDueAlerts();
                allAlerts.push(...pastDue.alerts);
              } catch (err) {
                console.error('Error loading system past-due alerts:', err);
              }
              break;
            case 'airport':
            case 'airportPastDue':
              if (airportId) {
                try {
                  const airportAlerts = await getAirportAlerts(airportId, {
                    pastDueOnly: alertSource === 'airportPastDue',
                  });
                  allAlerts.push(...airportAlerts.alerts);
                } catch (err) {
                  console.error('Error loading airport alerts:', err);
                }
              }
              break;
            case 'user':
            case 'userPastDue':
              if (userId && isIntegerUserId(userId)) {
                try {
                  const userAlerts = await getUserAlerts(userId as number, {
                    pastDueOnly: alertSource === 'userPastDue',
                  });
                  allAlerts.push(...userAlerts.alerts);
                } catch (err) {
                  console.error('Error loading user alerts:', err);
                }
              } else if (userId && typeof userId === 'string') {
                console.warn('User alerts skipped: userId is a GUID (fallback)');
              }
              break;
            default:
              break;
          }
        } else {
          // Legacy: multiple sources based on userId, airportId, isAdmin
          if (userId && isIntegerUserId(userId)) {
            try {
              const userAlerts = await getUserAlerts(userId as number);
              allAlerts.push(...userAlerts.alerts);
            } catch (err) {
              console.error('Error loading user alerts:', err);
            }
          } else if (userId && typeof userId === 'string') {
            console.warn('User alerts skipped: userId is a GUID (fallback)');
          }

          if (airportId) {
            try {
              const airportAlerts = await getAirportAlerts(airportId);
              allAlerts.push(...airportAlerts.alerts);
            } catch (err) {
              console.error('Error loading airport alerts:', err);
            }
          }

          if (isAdmin) {
            try {
              const systemAlerts = await getSystemAlerts();
              allAlerts.push(...systemAlerts.alerts);
            } catch (err) {
              console.error('Error loading system alerts:', err);
            }
          }
        }

        // Sort by severity: CRITICAL > WARNING > INFO
        const severityOrder = { CRITICAL: 0, WARNING: 1, INFO: 2 };
        allAlerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

        setAlerts(allAlerts);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load alerts');
        console.error('Error loading alerts:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAlerts();

    // Sprint B1: skip refresh when a modal is open
    const interval = setInterval(() => {
      if (!isModalOpen) loadAlerts();
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userId, airportId, isAdmin, alertSource, isModalOpen]);

  const handleDismiss = (alertIndex: number) => {
    const alert = alerts[alertIndex];
    // Only allow dismissing non-critical alerts
    if (alert.severity !== 'CRITICAL') {
      setDismissedAlerts((prev) => new Set(prev).add(alertIndex));
    }
  };

  const handleNavigate = (actionUrl: string) => {
    navigate(actionUrl);
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' => {
    switch (severity) {
      case 'CRITICAL':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'INFO':
        return 'info';
      default:
        return 'info';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <Error />;
      case 'WARNING':
        return <Warning />;
      case 'INFO':
        return <Info />;
      default:
        return <Info />;
    }
  };

  const visibleAlerts = alerts.filter((_, index) => !dismissedAlerts.has(index));

  if (loading) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={60}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (visibleAlerts.length === 0) {
    return null; // Don't show alerts section if no alerts
  }

  return (
    <Paper sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Alerts</Typography>
          <Chip
            label={visibleAlerts.length}
            color={visibleAlerts.some((a) => a.severity === 'CRITICAL') ? 'error' : 'warning'}
            size="small"
          />
        </Box>
        <IconButton size="small" onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <List sx={{ p: 0 }}>
            {visibleAlerts.map((alert, index) => {
              const originalIndex = alerts.indexOf(alert);
              const severityColor = getSeverityColor(alert.severity);
              const canDismiss = alert.severity !== 'CRITICAL';

              return (
                <ListItem
                  key={`${alert.type}-${alert.entityId}-${index}`}
                  sx={{
                    mb: 1,
                    p: 0,
                  }}
                >
                  <Alert
                    severity={severityColor}
                    icon={getSeverityIcon(alert.severity)}
                    sx={{ width: '100%' }}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {alert.actionUrl && (
                          <Button
                            size="small"
                            onClick={() => handleNavigate(alert.actionUrl)}
                            sx={{ mr: 1 }}
                          >
                            View
                          </Button>
                        )}
                        {canDismiss && (
                          <IconButton
                            size="small"
                            onClick={() => handleDismiss(originalIndex)}
                            sx={{ ml: 'auto' }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    }
                  >
                    <AlertTitle>{alert.message}</AlertTitle>
                    {alert.entityId != null && (
                      <Typography variant="body2" color="text.secondary">
                        {alert.entityType.charAt(0).toUpperCase() + alert.entityType.slice(1)} #{alert.entityId}
                      </Typography>
                    )}
                  </Alert>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
}

